<template>
  <div>
    <ModalComponent
      name="manage-assets"
      :shift-x="0.97"
      height="auto"
      :adaptive="true"
      :scrollable="true"
      :click-to-close="false"
    >
      <div v-if="fetchingData">
        <div class="flex items-center justify-center w-full h-screen">
          <PreloaderIcon class="mx-auto text-zuno-turquoise" />
        </div>
      </div>
      <form
        v-else
        class="p-6"
        @submit.prevent="saveAsset"
      >
        <div class="flex items-center justify-between pb-4 border-[#EDEDED] border-b">
          <div class="gap-1.5">
            <h3 class="text-lg font-semibold text-primary lg:text-xl">
              {{ editing ? 'Edit' : 'Create' }} Asset
            </h3>
            <p class="text-sm text-primary/50">
              Please fill all the fields to {{ editing ? 'edit' : 'create' }} your
              asset
            </p>
          </div>
          <button
            class="w-12 h-12 flex items-center justify-center outline-none bg-[#EEEFF0D4] text-[#808080] rounded-md"
            @click="cancelAssetManager"
          >
            <TimesSmall />
          </button>
        </div>

        <div class="flex flex-col mt-8 mb-16 gap-y-3">
          <div class="flex flex-col gap-y-1">
            <div class="flex items-center gap-1">
              <label
                for="asset-name"
                class="text-sm font-medium"
              >Asset name<span class="text-red-600">*</span></label>
              <HelperText
                title="You may input the naming for your business units, plants, subsidiaries, companies, item of the inventories etc., to indicate the representation of the node."
              />
            </div>
            <TextInput
              id="asset-name"
              type="text"
              :val="orgAsset.asset_name"
              :required="true"
              :disabled="parentFieldsAreFixed"
              @input="orgAsset.asset_name = $event"
            />
            <small
              v-if="errorMessage"
              class="block mt-1 mb-2 font-medium text-red-primary"
            >{{ errorMessage }}</small>
          </div>

          <div class="flex flex-col gap-y-1">
            <label
              for="asset-description"
              class="text-sm font-medium"
            >Description</label>
            <TextareaInput
              id="asset-description"
              :val="orgAsset.description"
              @input="orgAsset.description = $event"
            />
          </div>

          <div
            v-if="setting !== 'operational_control'"
            class="flex flex-col gap-y-1"
          >
            <div class="flex items-center gap-1">
              <label
                for="asset-ownership"
                class="text-sm font-medium"
              >% Ownership</label>
              <HelperText :title="`Please input your % ${ownershipName} `" />
            </div>
            <TextInput
              id="asset-ownership"
              type="text"
              :val="orgAsset.ownership"
              :disabled="parentFieldsAreFixed"
              @input="orgAsset.ownership = $event"
            />
            <small
              v-if="Number(orgAsset.ownership) < 0 || Number(orgAsset.ownership) > 100"
              class="block mt-1 mb-2 font-medium text-red-primary"
            >Only values from 0 to 100 are valid</small>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium">Geolocation<span class="text-red-600">*</span></label>
            <DropDownInput
              v-if="orgAsset.geolocation"
              display-key="region_name"
              value-key="region_code"
              :options="regions"
              :first-value="orgAsset.geolocation"
              max-width-class="max-w-full"
              :translate-labels="true"
              @value-updated="orgAsset.geolocation = $event"
            />
            <DropDownInput
              v-else
              display-key="region_name"
              value-key="region_code"
              :options="regions"
              max-width-class="max-w-full"
              :translate-labels="true"
              @value-updated="orgAsset.geolocation = $event"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label
              class="text-sm font-medium"
              for="asset-ownership-date"
            >Date of Ownership</label>
            <TextInput
              id="asset-ownership-date"
              type="date"
              :val="orgAsset.ownership_date"
              @input="orgAsset.ownership_date = $event"
            />
          </div>
          <div
            v-if="orgAsset.asset_classification || (!orgAsset.asset_classification && userIsAdmin)"
            class="flex flex-col gap-1"
          >
            <div class="flex justify-between gap-x-3">
              <div>
                <label class="text-sm font-medium">Asset Class<span class="text-red-600">*</span></label>
              </div>
              <div class="flex items-center gap-2">
                <input
                  id="approver-checkbox"
                  v-model="pcafStatus"
                  type="checkbox"
                  class="p-2 bg-white border-2 border-gray-400 rounded-sm cursor-pointer hover:border-gray-500 text-main-primary focus:ring-transparent focus:border-main-primary"
                >
                <label class="text-sm font-medium">Scope 3 Cat 15 / PCAF 's class required </label>
              </div>
            </div>
            <div
              v-if="oldPcafValue && !pcafStatus"
              class="block mt-1 mb-2 font-medium text-red-primary"
            >
              The asset class cannot be unselected or empty
            </div>
            <DropDownInput
              :key="`asset-class-key-${assetClassKey}`"
              display-key="title"
              value-key="id"
              :options="assetClasses"
              :first-value="orgAsset.asset_classification?.id ? orgAsset.asset_classification.id : orgAsset.asset_classification"
              max-width-class="max-w-full"
              :translate-labels="true"
              @value-updated="orgAsset.asset_classification = $event"
            />
          </div>
          <div v-if="selectedCatgories.length">
            <label
              for="Assets"
              class="flex justify-start mt-2"
            >
              <span class="text-sm font-semibold">Emission Categories</span>
            </label>
            <div class="flex flex-wrap items-start w-full gap-3">
              <span
                v-for="category in selectedCatgories"
                :key="category.id"
                class="bg-[#ECFDF3] px-3 py-1 whitespace-nowrap rounded-md text-[#027A48] border border-[#ECFDF3] capitalize text-sm font-normal"
              >{{
                category.name }}</span>
            </div>
          </div>
          <div v-if="selectedAssets.length">
            <label
              for="topic"
              class="flex justify-start my-2"
            >
              <span class="text-sm font-semibold">ESG Metrics</span>
            </label>
            <div class="flex flex-wrap items-start w-full gap-3">
              <span
                v-for="metric in selectedAssets"
                :key="metric.metric_id"
                class="bg-[#ECFDF3] px-3 py-1 whitespace-nowrap rounded-md text-[#027A48] border border-[#ECFDF3] capitalize text-sm font-normal no-localize-translate"
              >{{
                metric.name }}</span>
            </div>
          </div>
        </div>

        <div class="pt-16 border-[#EDEDED] border-t flex items-center justify-end gap-5">
          <GhostButton @click="cancelAssetManager">
            Cancel
          </GhostButton>
          <PrimaryButton
            type="submit"
            aria-label="Save"
            :disabled="cantSaveAsset"
          >
            Save changes
          </PrimaryButton>
        </div>
      </form>
    </ModalComponent>
  </div>
</template>

<script>
export default {
  name: 'ManageAssetsModal',

  props: {
    asset: {
      type: Object,
      required: true,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    setting: {
      type: String,
      required: true,
    },
    emissionCategories: {
      type: Array,
      required: true,
    },
    regions: {
      type: Array,
      required: true,
    },
  },

  emits: ['savingAsset', 'refresh', 'close'],

  data() {
    return {
      selectedCatgories: [],
      selectedAssets: [],
      errorMessage: null,
      timesCategoriesChanged: 0,
      frequencies: [
        { name: 'Daily', id: 'daily' },
        { name: 'Weekly', id: 'weekly' },
        { name: 'Biweekly', id: 'biweekly' },
        { name: 'Monthly', id: 'monthly' },
        { name: 'Quarterly', id: 'quarterly' },
        { name: 'Yearly', id: 'yearly' },
      ],
      orgAsset: this.editing
        ? {
            ...this.asset,
            ...(this.asset.geolocation !== null && { geolocation: this.asset.geolocation.region_code }),
          }
        : {
            parent_node: this.asset.id,
            data_collection_frequency: '',
            ownership_date: '',
            geolocation: '',
            ownership: '',
            description: '',
            asset_name: '',
            asset_classification: null,
          },
      fetchingData: false,
      assetClasses: [],
      pcafStatus: false,
      assetClassKey: 0,
      oldPcafValue: false,
    }
  },

  computed: {
    cantSaveAsset() {
      const { asset_name, geolocation, ownership, asset_classification } = this.orgAsset
      const assetClassUnset = (this.editing && this.userIsAdmin && !asset_classification) || (!this.editing && !asset_classification)
      return !asset_name || !geolocation || Number(ownership) < 0 || Number(ownership) > 100 || assetClassUnset || (!this.pcafStatus && this.oldPcafValue)
    },
    parentFieldsAreFixed() {
      return this.editing && !this.orgAsset.parent_node
    },
    ownershipName() {
      const First = this.setting[0].toUpperCase()
      const rest = this.setting.slice(1)
      return (First + rest).replace(/_/g, ' ')
    },
    userIsAdmin() {
      const companyId = localStorage.getItem('cid')
      const companies = JSON.parse(localStorage.getItem('companies'))
      return companies.find(({ id }) => id === Number(companyId))?.is_member_admin
    },
  },

  watch: {
    asset: {
      immediate: true,
      deep: true,
      handler(newAsset) {
        if (newAsset) {
          this.orgAsset = this.editing
            ? {
                ...newAsset,
                ...(newAsset.geolocation !== null && { geolocation: newAsset.geolocation.region_code }),
              }
            : {
                parent_node: newAsset.id,
                data_collection_frequency: '',
                ownership_date: '',
                geolocation: '',
                ownership: '',
                asset_classification: null,
                description: '',
                asset_name: '',
              }
          this.pcafStatus = this.editing && this.orgAsset.asset_classification ? this.orgAsset.asset_classification?.pcaf : false
          this.oldPcafValue = this.editing && this.orgAsset.asset_classification ? this.orgAsset.asset_classification?.pcaf : false
        }
      },
    },
    orgAsset: {
      immediate: true,
      handler(asset) {
        if (asset.emission_categories) {
          this.selectedCatgories = asset.emission_categories
          this.selectedAssets = asset.esg_metrics
        }
      },
    },
    pcafStatus(newValue) {
      this.orgAsset.asset_classification = null
      this.getAssetClasses(newValue)
    },
  },

  async mounted() {
    this.$modal.show('manage-assets')
    this.fetchingData = true
    await this.getAssetClasses(this.pcafStatus)
    this.fetchingData = false
  },

  methods: {
    saveAsset() {
      this.$emit('savingAsset', { status: true })
      this.orgAsset.asset_classification = this.editing && this.orgAsset.asset_classification.id > 0 ? this.orgAsset.asset_classification.id : this.orgAsset.asset_classification
      const data = { ...this.orgAsset };

      ['description', 'ownership', 'ownership_date'].forEach((key) => {
        if (!data[key]) {
          data[key] = null
        }
      })

      if (!this.editing) {
        this.$apis.assets
          .create(data)
          .then(() => {
            this.$modal.hide('manage-assets')
            this.$toast.success(
              `Your asset ${data.asset_name} has been created successfully!`,
              {
                timeout: 3000,
                position: 'top-right',
              },
            )
            this.$emit('refresh')
          })
          .finally(() => this.$emit('savingAsset', { status: false }))
      }
      else {
        this.$apis.assets
          .update(this.asset.id, data)
          .then(() => {
            this.$modal.hide('manage-assets')
            this.$toast.success(
              `Your asset ${data.asset_name} has been updated successfully!`,
              {
                timeout: 3000,
                position: 'top-right',
              },
            )
            this.$emit('refresh')
          })
          .finally(() => this.$emit('savingAsset', { status: false }))
      }
    },
    cancelAssetManager() {
      this.$modal.hide('manage-assets')
      this.$emit('close')
    },
    getAssetClasses(checkStatus) {
      return this.$apis.assets.assetClasses(checkStatus).then(({ data: assetsData }) => {
        this.assetClasses = assetsData
        this.assetClassKey++
      })
    },
  },
}
</script>
